import { formatMoney, parseMoney, type Centavos } from '@abastece/shared';
import { useT } from '../i18n/index.js';

export type Moneda = 'NIO' | 'USD';

/**
 * The notes of each currency, largest first, in cents. Córdobas: the C$ 1,000 to C$ 10 notes, with
 * the coins counted together as one amount (the C$ 10 coin among them). Dollars: the bills a shop
 * takes; nobody pays in dollar coins.
 */
export const BILLETES: Record<Moneda, readonly number[]> = {
  NIO: [100_000, 50_000, 20_000, 10_000, 5_000, 2_000, 1_000],
  USD: [10_000, 5_000, 2_000, 1_000, 500, 100],
};

/** A drawer counted note by note: how many of each note, as typed, and the coins as an amount. */
export interface Conteo {
  billetes: Readonly<Record<number, string>>;
  monedas: string;
}

export const CONTEO_VACIO: Conteo = { billetes: {}, monedas: '' };

/** How many of one note were typed: a whole number, empty being none. Null when it is not one. */
function cuantos(texto: string | undefined): number | null {
  const limpio = (texto ?? '').trim();
  if (limpio === '') return 0;
  return /^\d{1,5}$/.test(limpio) ? Number(limpio) : null;
}

/** The count's total, in cents of its currency. Null while something typed is not a number. */
export function totalDelConteo(conteo: Conteo, moneda: Moneda): Centavos | null {
  let total = 0;
  for (const valor of BILLETES[moneda]) {
    const n = cuantos(conteo.billetes[valor]);
    if (n === null) return null;
    total += n * valor;
  }
  if (moneda === 'NIO' && conteo.monedas.trim() !== '') {
    const m = parseMoney(conteo.monedas);
    if (m === null || m < 0) return null;
    total += m;
  }
  return total as Centavos;
}

/** Anything typed at all: a count nobody started is not taken for an empty drawer. */
export const conteoEmpezado = (conteo: Conteo) =>
  Object.values(conteo.billetes).some((v) => v.trim() !== '') || conteo.monedas.trim() !== '';

/** An amount of either currency as the drawer holds it: «C$ 1,250.00», «US$ 40.00». */
export const enMoneda = (c: Centavos, moneda: Moneda) =>
  moneda === 'NIO' ? formatMoney(c) : `US$ ${formatMoney(c, { symbol: false })}`;

/** A note's name: «C$ 1,000», «US$ 20». */
const nombreBillete = (valor: number, moneda: Moneda) =>
  `${moneda === 'NIO' ? 'C$' : 'US$'} ${(valor / 100).toLocaleString('en-US')}`;

/**
 * Contar la gaveta, billete por billete.
 *
 * Whoever counts types how many there are of each note, and the coins as one amount, and the
 * screen does the adding up: a total added in somebody's head at the end of a long day is where a
 * miscount comes from, and a miscount at the corte is a shortage in somebody's name. Only what was
 * counted is on screen, never what should be there: the count stays blind.
 */
export function ContarBilletes({
  moneda,
  conteo,
  onCambio,
}: {
  moneda: Moneda;
  conteo: Conteo;
  onCambio: (conteo: Conteo) => void;
}) {
  const t = useT();
  const total = totalDelConteo(conteo, moneda);
  return (
    <div className="flex flex-col gap-3">
      <ul className="tarjeta flex flex-col divide-y divide-slate-800/70 p-0">
        {BILLETES[moneda].map((valor) => {
          const texto = conteo.billetes[valor] ?? '';
          const n = cuantos(texto);
          const nombre = nombreBillete(valor, moneda);
          return (
            <li key={valor} className="flex items-center gap-3 px-4 py-2">
              <span className="tabular w-20 shrink-0 font-semibold">{nombre}</span>
              <input
                className={`campo tabular min-h-12 w-20 shrink-0 px-2 text-center text-lg ${n === null ? 'ring-rose-600' : ''}`}
                inputMode="numeric"
                autoComplete="off"
                placeholder="0"
                aria-label={t('contar.billetesDe', { billete: nombre })}
                value={texto}
                onChange={(e) => onCambio({ ...conteo, billetes: { ...conteo.billetes, [valor]: e.target.value } })}
              />
              <span className="tabular min-w-0 flex-1 truncate text-right text-sm text-slate-400">
                {n ? `= ${enMoneda((n * valor) as Centavos, moneda)}` : ''}
              </span>
            </li>
          );
        })}
        {moneda === 'NIO' && (
          <li className="flex items-center gap-3 px-4 py-2">
            <span className="w-20 shrink-0 font-semibold">{t('contar.monedas')}</span>
            <input
              className={`campo tabular min-h-12 min-w-0 flex-1 px-3 text-right text-lg ${
                conteo.monedas.trim() !== '' && (parseMoney(conteo.monedas) ?? -1) < 0 ? 'ring-rose-600' : ''
              }`}
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              aria-label={t('contar.monedasEtiqueta')}
              value={conteo.monedas}
              onChange={(e) => onCambio({ ...conteo, monedas: e.target.value })}
            />
          </li>
        )}
      </ul>
      {moneda === 'NIO' && <p className="text-xs text-slate-500">{t('contar.monedasAyuda')}</p>}
      <div className="flex items-baseline justify-between gap-3 rounded-2xl bg-slate-800/60 px-4 py-3" role="status">
        <span className="font-medium">{t('contar.total')}</span>
        <span className="tabular text-2xl font-bold">{total === null ? t('contar.revise') : enMoneda(total, moneda)}</span>
      </div>
    </div>
  );
}
